import { Link } from "react-router-dom";
import type { Product } from "@/api/productApi";
import { assetUrl } from "@/utils/assetUrl";

interface Props {
  products: Product[];
  accentColor: string;
}

const typeLabels: Record<string, string> = {
  hardware: "硬件",
  software: "软件",
  service: "服务",
};

export default function DetailProducts({ products, accentColor }: Props) {
  if (products.length === 0) return null;

  return (
    <section className="py-16 lg:py-20 bg-white">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        {/* Section header */}
        <div className="flex items-center justify-between mb-10">
          <div className="flex items-center gap-3">
            <div className="w-1 h-6 rounded-full" style={{ backgroundColor: accentColor }}></div>
            <div>
              <p className="text-xs text-gray-400 font-medium tracking-wider uppercase mb-0.5">Related Products</p>
              <h2 className="text-xl font-bold text-[#0d1f3c]">方案相关产品</h2>
            </div>
          </div>
          <Link
            to="/products"
            className="flex items-center gap-1.5 text-sm font-medium hover:gap-2.5 transition-all duration-200 whitespace-nowrap cursor-pointer"
            style={{ color: accentColor }}
          >
            查看全部产品
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-right-line text-sm"></i>
            </div>
          </Link>
        </div>

        {/* Products grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
          {products.map((product) => (
            <Link
              key={product.id}
              to={`/products/${product.id}`}
              className="group bg-white rounded-2xl border border-gray-100 overflow-hidden transition-all duration-300 hover:border-opacity-60 cursor-pointer block"
              style={{ borderColor: `${accentColor}15` }}
            >
              {/* Product image */}
              <div className="relative w-full h-36 overflow-hidden bg-gray-50">
                <img
                  src={assetUrl(product.image)}
                  alt={product.name}
                  className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                />
                {/* Type badge(s) */}
                <div className="absolute top-3 left-3 flex flex-wrap gap-1">
                  {((product.type ?? []) as string[]).map((t) => (
                    <span
                      key={t}
                      className="px-2 py-0.5 text-xs font-medium rounded-full text-white"
                      style={{ backgroundColor: `${accentColor}cc` }}
                    >
                      {typeLabels[t] || t}
                    </span>
                  ))}
                </div>
                {/* Hover overlay */}
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-all duration-300 flex items-center justify-center">
                  <div
                    className="w-9 h-9 flex items-center justify-center rounded-full opacity-0 group-hover:opacity-100 transition-all duration-300 translate-y-2 group-hover:translate-y-0"
                    style={{ backgroundColor: accentColor }}
                  >
                    <i className="ri-arrow-right-up-line text-white text-sm"></i>
                  </div>
                </div>
              </div>

              {/* Product info */}
              <div className="p-4">
                {/* Tag + Model in a row */}
                <div className="flex items-center gap-2 mb-1.5">
                  <span
                    className="text-xs px-2 py-0.5 rounded-full border"
                    style={{
                      backgroundColor: `${accentColor}08`,
                      color: accentColor,
                      borderColor: `${accentColor}20`,
                    }}
                  >
                    {product.tag}
                  </span>
                  {product.model && (
                    <span className="text-xs text-gray-400 truncate">{product.model}</span>
                  )}
                </div>

                {/* Name */}
                <h3 className="text-sm font-bold text-[#0d1f3c] mb-1.5 line-clamp-1 group-hover:text-[#1a56db] transition-colors">
                  {product.name}
                </h3>

                {/* Desc */}
                <p className="text-xs text-gray-500 leading-relaxed line-clamp-2 mb-2.5">{product.desc}</p>

                {/* Highlights */}
                <div className="flex flex-wrap gap-1">
                  {product.highlights.slice(0, 2).map((h) => (
                    <span key={h} className="text-xs text-gray-400 bg-gray-50 px-2 py-0.5 rounded-md">
                      {h}
                    </span>
                  ))}
                </div>

                {/* CTA */}
                <div
                  className="flex items-center gap-1.5 text-xs font-semibold mt-2.5 pt-2.5 border-t border-gray-50 group-hover:gap-2.5 transition-all duration-200"
                  style={{ color: accentColor }}
                >
                  <span>查看产品详情</span>
                  <div className="w-3.5 h-3.5 flex items-center justify-center">
                    <i className="ri-arrow-right-line text-xs"></i>
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}
